import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import { Success } from 'app/components/Notifications';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import useClickOutside from 'app/hooks/useClickOutside';
import clsx from 'clsx';
import { useNotificationsContext } from 'context';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { userInfoApi } from 'services/api/user-info/queries';
import { formatPhone, unmaskPhone } from 'utils/text/phoneMask';
import z from 'zod';

interface Props {
  initialValue: string;
  onChange: (value: string) => void;
  close: () => void;
}

const phoneSchema = z.object({
  phone: z
    .string()
    .min(1, { message: 'phone_is_required' })
    .refine(val => unmaskPhone(val).length === 10, {
      message: 'phone_incorrect_error',
    }),
});

type FormData = z.infer<typeof phoneSchema>;

export default function EditPhoneNumberDialog({
  initialValue,
  onChange,
  close,
}: Props) {
  const { t } = useTranslation();

  const [updateUser, { isLoading }] = userInfoApi.useUpdateUserInfoMutation();
  const { addErrorModal } = React.useContext(GlobalErrorContext);
  const { addNotification } = useNotificationsContext();

  // Note: there's a point in creating a wrapper around the Dialog and adding an onClickOutside prop
  useClickOutside(close);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(phoneSchema),
    values: {
      phone: initialValue ? formatPhone(initialValue) : '',
    },
  });

  async function onSubmit({ phone }: FormData) {
    const cleanPhone = unmaskPhone(phone);
    try {
      await updateUser({
        phoneNumber: cleanPhone,
      });
      addNotification(<Success text={t('success_phone_number_update')} />);
      onChange(cleanPhone);
      close();
    } catch (error) {
      addErrorModal();
    }
  }

  return (
    <Dialog className="modal-form">
      <div className="modal-container relative">
        <span
          className="back-button absolute"
          onClick={() => {
            close();
          }}
        >
          <svg
            id="ic_nav_back"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
          >
            <rect id="Box" width="48" height="48" fill="none" />
            <path
              id="ic_nav_up"
              d="M16.465,8.482H1.483"
              transform="translate(15.518 15.518)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
            <path
              id="Path_470"
              data-name="Path 470"
              d="M23.676,16.018,16.2,23.748l7.477,7.73"
              transform="translate(0.324 0.252)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </svg>
        </span>
        <>
          <h2>{t('edit_phone_number')}</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={clsx('form-group', { error: !!errors.phone })}>
              <div className="form-content">
                <div className="form-label">
                  <span>{t('phone_number')}*</span>
                </div>

                <div className="form-control">
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        autoComplete="off"
                        placeholder={t('enter_your_phone_number')}
                        disabled={isLoading}
                        onChange={e => {
                          const inputValue = e.target.value as string;
                          const formatted = formatPhone(inputValue);
                          field.onChange(formatted);
                        }}
                      />
                    )}
                  />
                </div>
              </div>

              {errors.phone?.message && (
                <div className="form-error">
                  <Error>{t<string>(errors.phone.message)}</Error>
                </div>
              )}
            </div>

            <Button themeColor="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <div className="loading_icon loading_icon-btn">
                  <div className="css-icon"></div>
                </div>
              ) : (
                t('save')
              )}
            </Button>
          </form>
        </>
      </div>
    </Dialog>
  );
}
